<template>
  <div id="user-management-container">
    <div class="modal fade confirmation-modal" id="user-management" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content item-card">
          <div class="modal-header">
            <h2 class="modal-title">User Management</h2>
          </div>
          <div class="modal-body">
            <section v-if="isOwner" class="invite-link mb-4" aria-labelledby="invite-link-heading">
              <h3 id="invite-link-heading">Invite people</h3>
              <p>Share this private link with people you want to join this list.</p>
              <button class="btn btn-green" data-testid="copy-invite-link" type="button" :disabled="inviteLoading" @click="copyInviteLink">
                {{ inviteLoading ? "Generating invite link..." : "Copy invite link" }}
              </button>
              <div v-if="inviteLink" class="form-group mt-3 mb-0">
                <label for="invite-link">Invite link</label>
                <input id="invite-link" ref="inviteLink" class="form-control" type="text" readonly :value="inviteLink" @focus="$event.target.select()" />
                <small v-if="clipboardFailed" class="form-text text-warning" role="alert">Copying failed. Select and copy the link above manually.</small>
              </div>
            </section>
            <div v-if="open" class="row mt-3 mb-3 justify-content-md-center">
              <div class="col-3" v-for="user in users" :key="user">
                <UserCard :user="user" :selectableRoles="selectableRoles" @save-user="saveUser" @delete-user="confirmDeleteUser" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <a class="btn btn-green" data-toggle="modal" data-target="#add-user">Add User</a>
            <a class="btn btn-red" data-dismiss="modal">Close</a>
          </div>
        </div>
      </div>
    </div>
    <UserAdd @add-user="addUser" />
    <DeleteConfirm
      v-if="deleteConfirm.user"
      @delete-confirmed="deleteUser"
      :value="deleteConfirm.user.displayName"
      :open="deleteConfirm.open"
      @closed="deleteConfirm.open = false" />
  </div>
</template>

<script>
import UserCard from "./UserCard";
import ListService from "../../services/ListService";
import { Role, hasRole } from "../../utilities/list.role";
import DeleteConfirm from "../../components/DeleteConfirm";
import UserAdd from "./UserAdd";
import $ from "jquery";

export default {
  name: "UserManagement",
  props: ["id"],
  emits: ["member-count"],
  data() {
    return {
      users: [],
      selectableRoles: [],
      deleteConfirm: {
        open: false,
        user: null,
      },
      open: false,
      inviteLoading: false,
      inviteLink: "",
      clipboardFailed: false,
    };
  },
  inject: ["userRole"],
  components: {
    UserCard,
    DeleteConfirm,
    UserAdd,
  },
  computed: {
    isOwner() {
      const userRole = this.userRole.value || this.userRole;
      return userRole && userRole.id === 1;
    },
  },
  mounted() {
    ListService.getUsers(this.id)
      .then((result) => {
        this.users = result.users;
        this.$emit("member-count", this.users.length);
      })
      .catch((error) => {
        return error.handleError();
      });

    const userRole = this.userRole.value || this.userRole;

    if (hasRole(userRole.id, Role.OWNER)) {
      this.selectableRoles.push({
        id: 3,
        roleName: "Member",
      });
    }

    var vm = this;

    $("#user-management").on("show.bs.modal", function () {
      vm.open = true;
    });

    $("#user-management").on("hidden.bs.modal", function () {
      vm.open = false;
    });
  },
  methods: {
    buildInviteLink(invite) {
      const value = typeof invite === "string" ? invite : invite.inviteUrl || invite.url || invite.link || invite.token;

      if (!value) {
        throw new Error("Invite response did not include a link or token");
      }

      if (/^https?:\/\//i.test(value)) {
        return value;
      }

      const path = value.startsWith("/") ? value : `/invites/${encodeURIComponent(value)}`;
      return `${window.location.origin}${path}`;
    },
    async copyInviteLink() {
      if (this.inviteLoading) {
        return;
      }

      this.inviteLoading = true;
      this.inviteLink = "";
      this.clipboardFailed = false;

      try {
        const invite = await ListService.createInvite(this.id);
        this.inviteLink = this.buildInviteLink(invite);

        if (!navigator.clipboard || !navigator.clipboard.writeText) {
          throw new Error("Clipboard API is unavailable");
        }

        await navigator.clipboard.writeText(this.inviteLink);
        this.$toast.success("Invite link copied");
      } catch (error) {
        if (this.inviteLink) {
          this.clipboardFailed = true;
          this.$toast.error("Invite link could not be copied. Please copy it manually.");
          this.$nextTick(() => this.$refs.inviteLink && this.$refs.inviteLink.select());
        } else if (error.response && error.response.status === 403) {
          return error.handleError();
        } else {
          this.$toast.error("Unable to generate an invite link. Please try again.");
        }
      } finally {
        this.inviteLoading = false;
      }
    },
    saveUser(userId, roleId) {
      ListService.saveUser(this.id, userId, roleId)
        .then(() => {
          this.$toast.success("Successfully Saved User");
        })
        .catch((error) => {
          return error.handleError();
        });
    },
    confirmDeleteUser(user) {
      this.deleteConfirm.user = user;
      this.deleteConfirm.open = true;
    },
    deleteUser() {
      var vm = this;

      ListService.deleteUser(this.id, this.deleteConfirm.user.id)
        .then(() => {
          this.$toast.success("Successfully Deleted User");
          vm.users = vm.users.filter((user) => user.id != vm.deleteConfirm.user.id);
          vm.$emit("member-count", vm.users.length);
        })
        .catch((error) => {
          return error.handleError();
        });
    },
    addUser(email) {
      var vm = this;

      ListService.addUser(this.id, email)
        .then((result) => {
          vm.users.push(result.user);
          vm.$emit("member-count", vm.users.length);

          this.$toast.success(`Successfully Added ${email}`);
        })
        .catch((error) => {
          return error.handleError();
        });
    },
  },
};
</script>

<style scoped>
.user-card .user-icon {
  font-size: 70px;
}

.user-card h5 {
  font-size: 20px;
  margin: 0;
}

.user-card .edit-icon {
  display: none;
}

.user-card:hover .edit-icon {
  position: absolute;
  right: 20px;
  color: lightgray;
  display: inline-block;
}

.user-card:hover .edit-icon:hover {
  color: whitesmoke;
}

.invite-link {
  border-bottom: 2px solid #393d55;
  padding-bottom: 1.5rem;
}

.invite-link h3 {
  font-size: 1.5rem;
}
</style>
